import React, { useState } from 'react';
import {
  Button,
  Popover,
  Switch,
  Space,
  Typography,
  Divider,
  Badge,
  Row,
  Col,
} from 'antd';
import { EyeOutlined, SettingOutlined } from '@ant-design/icons';
import { SmartTableColumn } from '../types';
import { createStyles } from 'antd-style';

const { Text } = Typography;

const useStyles = createStyles(({ css, token }) => {
  const { antCls } = token as any;
  return {
    columnVisibilityButton: css`
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid ${token.colorBorder};
      border-radius: 6px;
      padding: 4px 8px;
      background: ${token.colorBgContainer};
      transition: all 0.2s ease;
      min-width: 32px;
      height: 32px;
      position: relative;

      &:hover {
        border-color: ${token.colorPrimary};
        color: ${token.colorPrimary};
        background: ${token.colorPrimaryBg};
      }

      &.active {
        border-color: ${token.colorPrimary};
        color: ${token.colorPrimary};
        background: ${token.colorPrimaryBg};
      }
    `,
    columnVisibilityButtonActive: css`
      border-color: ${token.colorPrimary};
      color: ${token.colorPrimary};
      background: ${token.colorPrimaryBg};
    `,
    popoverContent: css`
      min-width: 400px;
      max-width: 480px;
      padding: 0;
    `,
    popoverHeader: css`
      padding: 12px 16px;
      border-bottom: 1px solid ${token.colorBorderSecondary};
      background: ${token.colorBgLayout};
      border-radius: 6px 6px 0 0;
    `,
    popoverBody: css`
      padding: 12px;
      max-height: 300px;
      overflow-y: auto;
    `,
    actionButtons: css`
      margin-bottom: 8px;

      .${antCls}-btn {
        font-size: 11px;
        height: 24px;
        padding: 0 8px;
      }
    `,
    switchGroup: css`
      .${antCls}-row {
        margin: 0 -4px;
      }

      .${antCls}-col {
        padding: 0 4px;
      }
    `,
    switchItem: css`
      display: flex;
      align-items: center;
      padding: 4px 8px;
      border-radius: 4px;
      margin-bottom: 2px;
      transition: all 0.2s ease;
      border: 1px solid transparent;

      &:hover {
        background: ${token.colorBgTextHover};
        border-color: ${token.colorBorderSecondary};
      }

      .column-title {
        font-size: 12px;
        font-weight: 500;
        color: ${token.colorText};
        flex: 1;
        margin-left: 8px;
        line-height: 1.3;
        text-align: left;
      }

      .${antCls}-switch {
        flex-shrink: 0;
        min-width: 24px;
        transform: scale(0.8);
      }
    `,
    badge: css`
      .${antCls}-badge-count {
        background: ${token.colorPrimary};
        font-size: 10px;
        font-weight: 600;
        min-width: 16px;
        height: 16px;
        line-height: 16px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        z-index: 10;
      }

      .${antCls}-badge {
        display: inline-block;
      }
    `,
  };
});

interface ColumnVisibilityComponentProps {
  columns: SmartTableColumn[];
  visibleColumns: string[];
  onColumnVisibilityChange: (visibleColumns: string[]) => void;
  onResetToDefault?: () => void;
  showColumnVisibility?: boolean;
}

export const ColumnVisibilityComponent: React.FC<
  ColumnVisibilityComponentProps
> = ({
  columns,
  visibleColumns,
  onColumnVisibilityChange,
  onResetToDefault,
  showColumnVisibility = true,
}) => {
  const { styles } = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  if (!showColumnVisibility) {
    return null;
  }

  const handleColumnToggle = (columnKey: string, checked: boolean) => {
    if (checked) {
      // Add column to visible columns
      const newVisibleColumns = [...visibleColumns, columnKey];
      onColumnVisibilityChange(newVisibleColumns);
    } else {
      // Remove column from visible columns
      const newVisibleColumns = visibleColumns.filter(key => key !== columnKey);
      onColumnVisibilityChange(newVisibleColumns);
    }
  };

  const handleSelectAll = () => {
    const allColumnKeys = columns.map(col => col.key);
    onColumnVisibilityChange(allColumnKeys);
  };

  const handleDeselectAll = () => {
    onColumnVisibilityChange([]);
  };

  const handleResetToDefault = () => {
    if (onResetToDefault) {
      onResetToDefault();
    } else {
      // Fallback: Show all columns by default
      const allColumnKeys = columns.map(col => col.key);
      onColumnVisibilityChange(allColumnKeys);
    }
  };

  const hiddenColumnsCount = columns.length - (visibleColumns || []).length;
  const isActive = hiddenColumnsCount > 0;

  const content = (
    <div className={styles.popoverContent}>
      <div className={styles.popoverHeader}>
        <Text strong style={{ fontSize: '14px', color: '#262626' }}>
          Column Visibility
        </Text>
        <Text
          style={{
            fontSize: '12px',
            color: '#8c8c8c',
            display: 'block',
            marginTop: '2px',
          }}
        >
          {(visibleColumns || []).length} of {columns.length} columns visible
        </Text>
      </div>

      <div className={styles.popoverBody}>
        <div className={styles.actionButtons}>
          <Space size="small" wrap>
            <Button size="small" type="primary" onClick={handleSelectAll}>
              Show All
            </Button>
            <Button size="small" onClick={handleDeselectAll}>
              Hide All
            </Button>
            <Button size="small" onClick={handleResetToDefault}>
              Reset
            </Button>
          </Space>
        </div>

        <div className={styles.switchGroup}>
          <Row gutter={[8, 4]}>
            {columns.map(column => (
              <Col span={12} key={column.key}>
                <div className={styles.switchItem}>
                  <Switch
                    size="small"
                    checked={(visibleColumns || []).includes(column.key)}
                    onChange={checked =>
                      handleColumnToggle(column.key, checked)
                    }
                  />
                  <span className="column-title" title={column.title}>
                    {column.title}
                  </span>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomRight"
      overlayStyle={{ zIndex: 1000 }}
    >
      <Badge
        count={hiddenColumnsCount}
        size="small"
        offset={[-4, 4]}
        className={styles.badge}
        showZero={false}
      >
        <Button
          icon={<SettingOutlined />}
          size="small"
          title="Column Visibility"
          className={`${styles.columnVisibilityButton} ${
            isActive ? styles.columnVisibilityButtonActive : ''
          }`}
        />
      </Badge>
    </Popover>
  );
};
